import React from 'react';
import { useTranslation } from 'react-i18next';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import Form from '@lib/components/ReactHookForm/FormContainer';
import { Steps } from '@lib/enums/form';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import { pageStepsId } from '@lib/enums/selectors';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import { UseSiteActionsResult } from '../utils/useSiteActions';
import getSiteFormFields from '../utils/getSiteFormFields';
import getSiteDefaultValuesFromQueryData from '../utils/getSiteDefaultValuesFromQueryData';
import SiteAddFormActions from './SiteAddFormActions';

interface Props {
  formRef: FormRefType;
  useSiteActionsResult: UseSiteActionsResult;
  useModalManagementResult: UseModalManagementResult;
}

function SiteAdd(props: Props) {
  const { formRef, useModalManagementResult, useSiteActionsResult } = props;
  const {
    siteQuery,
    countriesCodes,
    onCreate,
    onCreateUserCallback,
    usersLoadOptions,
    sectorsLoadOptions,
    responsiblePersonLoadOptions,
    createMutationResult,
  } = useSiteActionsResult;
  const { openModal, closeModal, isModalOpened } = useModalManagementResult;
  const openUserFormModal = () => openModal(Modals.UserFormModal);
  const closeUserFormModal = () => closeModal(Modals.UserFormModal);

  const { t } = useTranslation();

  const fields = getSiteFormFields({
    countriesCodes,
    usersLoadOptions,
    sectorsLoadOptions,
    openUserFormModal,
    responsiblePersonLoadOptions,
    t,
    assigneeIds: [],
  });

  const defaultValues = getSiteDefaultValuesFromQueryData({
    t,
  });

  if (siteQuery.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteQuery.error)}
        description={siteQuery.error?.message}
      />
    );
  }

  return (
    <>
      <div id={pageStepsId} />
      <DashboardContentMaxWidth>
        <PageBody withSteps>
          <WhiteBox verticalPaddings="large">
            <Form
              actionsComponent={SiteAddFormActions}
              defaultValues={defaultValues}
              fields={fields}
              formId="site"
              formRef={formRef}
              graphQLErrors={createMutationResult.error?.graphQLErrors}
              graphQLErrorsReplacePathMap={{
                'geojson.features': SiteFieldNames.LocationPoint,
              }}
              loading={createMutationResult.loading}
              onSubmit={onCreate}
              steps={[
                { title: t('site-step-1') },
                { title: t('site-step-2'), optional: true },
                { title: t('site-step-3'), optional: true },
              ]}
              stepsCount={Steps.step3}
              sourceAppendValues={{
                areas: {
                  name: '',
                  code: '',
                  address: '',
                  description: '',
                },
                conductCodeSections: { title: '', body: '' },
              }}
            />
          </WhiteBox>
        </PageBody>
      </DashboardContentMaxWidth>
      <UserModalForm
        isOpen={isModalOpened(Modals.UserFormModal)}
        onClose={closeUserFormModal}
        onSubmitUserFormCallback={onCreateUserCallback}
        title={t('add-team-member')}
      />
    </>
  );
}

export default SiteAdd;
